/**
 * IMPORTS
 */

export type IMetadadoCampoVariavel = { nome: string; obrigatorio: boolean };

function handleObterValorCelulaPorNomeColuna(
  linha: Record<string, unknown>,
  nomeColuna: string
): unknown {
  if (Object.hasOwn(linha, nomeColuna)) {
    return linha[nomeColuna];
  }
  const nomeColunaMinusculo = nomeColuna.toLowerCase();
  const nomeColunaEncontrada = Object.keys(linha).find(
    (nomePropriedadeNaLinha) => nomePropriedadeNaLinha.toLowerCase() === nomeColunaMinusculo
  );
  return nomeColunaEncontrada !== undefined ? linha[nomeColunaEncontrada] : undefined;
}

/**
 * Normaliza uma linha importada de planilha (Excel/CSV),
 * garantindo consistência de colunas com base no modelo esperado.
 *
 * O que essa função faz:
 * - Garante que todas as colunas esperadas existam na linha
 * - Normaliza nomes de colunas (case-insensitive: CPF, cpf, Cpf → CPF)
 * - Remove colunas duplicadas ou inválidas
 * - Preenche valores ausentes com string vazia ("")
 *
 * @param linha Objeto representando uma linha da planilha (ex: { cpf: "123", Nome: "João" })
 * @param nomeCampoChave Nome da coluna principal (ex: "CPF")
 * @param camposVariaveisDoModelo Lista de campos dinâmicos esperados no modelo
 *
 * @returns Nova linha com colunas padronizadas
 */
function handleNormalizarLinhaPlanilhaImportada(
  linha: Record<string, unknown>,
  nomeCampoChave: string,
  camposVariaveisDoModelo: IMetadadoCampoVariavel[]
): Record<string, unknown> {
  // clona a linha original para evitar mutação direta
  const linhaComColunasPadronizadas: Record<string, unknown> = { ...linha };

  // lista de colunas esperadas: campo chave (ex: CPF) e campos variáveis do modelo
  const nomesColunasEsperadas = [
    nomeCampoChave,
    ...camposVariaveisDoModelo.map((campo) => campo.nome),
  ];

  // mapa para normalização de nomes de colunas ignorando maiúsculo/minúsculo. ex: "cpf" → "CPF" e "nome" → "Nome"
  const mapaNomeMinusculoParaNomeColuna = new Map<string, string>();

  for (const nome of nomesColunasEsperadas) {
    mapaNomeMinusculoParaNomeColuna.set(nome.toLowerCase(), nome);
  }

  // garante que todas as colunas esperadas existam na linha. caso não exista, adiciona com valor vazio ("").
  for (const nomeColuna of nomesColunasEsperadas) {
    const valorCelula = handleObterValorCelulaPorNomeColuna(linha, nomeColuna);

    linhaComColunasPadronizadas[nomeColuna] = valorCelula === undefined ? "" : valorCelula;
  }

  // remove colunas inválidas ou duplicadas (ex: cpf + CPF), mantendo apenas o nome oficial definido no modelo.
  for (const propriedadeNaLinha of Object.keys(linhaComColunasPadronizadas)) {
    const nomeColunaOficial = mapaNomeMinusculoParaNomeColuna.get(propriedadeNaLinha.toLowerCase());

    // Se existe um nome oficial e ele é diferente do atual → remove
    if (nomeColunaOficial && propriedadeNaLinha !== nomeColunaOficial) {
      delete linhaComColunasPadronizadas[propriedadeNaLinha];
    }
  }

  /**
   * Retorna a linha totalmente normalizada
   */
  return linhaComColunasPadronizadas;
}

/**
 * EXPORTS
 */
export { handleNormalizarLinhaPlanilhaImportada, handleObterValorCelulaPorNomeColuna };
